import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, ArrowLeft, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import EmailGate, { useEmailGate } from '../../components/EmailGate';

const buildUrl = ({ data, size, fg, bg, ecc, margin }) => {
  const params = new URLSearchParams({
    data: data || 'https://aroundtheworldai.com',
    size: `${size}x${size}`,
    color: fg.replace('#', ''),
    bgcolor: bg.replace('#', ''),
    ecc,
    qzone: margin,
    format: 'png',
  });
  return `https://api.qrserver.com/v1/create-qr-code/?${params.toString()}`;
};

export default function QRCodeGeneratorPage() {
  const navigate = useNavigate();
  const [data, setData] = useState('https://aroundtheworldai.com');
  const [size, setSize] = useState(500);
  const [fg, setFg] = useState('#030303');
  const [bg, setBg] = useState('#ffffff');
  const [ecc, setEcc] = useState('H');
  const [margin, setMargin] = useState(4);
  const gate = useEmailGate('qr-code');

  const qrUrl = buildUrl({ data, size, fg, bg, ecc, margin });

  const download = async () => {
    gate.run(async () => {
      try {
        toast({ title: 'Downloading QR code…' });
        const r = await fetch(qrUrl);
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qr-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'QR code downloaded ✅' });
      } catch (e) {
        toast({ title: 'Failed to download', description: e.message });
      }
    });
  };

  const PRESETS = [
    { name: 'Cyan', fg: '#00F0FF', bg: '#030303' },
    { name: 'Magenta', fg: '#FF007F', bg: '#030303' },
    { name: 'Emerald', fg: '#10b981', bg: '#ffffff' },
    { name: 'Classic', fg: '#000000', bg: '#ffffff' },
    { name: 'Rose', fg: '#f43f5e', bg: '#ffffff' },
    { name: 'Chrome', fg: '#0f172a', bg: '#f1f5f9' },
  ];

  return (
    <div className="min-h-screen bg-[#050510] text-white">
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button data-testid="qr-back" onClick={() => navigate('/')} className="text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-[#00F0FF] font-semibold">QR Code Generator</span>
          <div className="ml-auto"><BrandLogo size={24} tone="light" /></div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-[400px_1fr] gap-10">
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <QrCode size={13} className="text-[#00F0FF]" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">QR Code Generator</span>
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Design a QR that pops.</h1>
            <p className="text-sm text-white/60 mt-1.5">Any URL, text or WiFi. Free forever, no watermark.</p>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">URL / Text / vCard</label>
            <textarea data-testid="qr-data" value={data} onChange={(e) => setData(e.target.value)} rows={3} className="mt-2 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-[#00F0FF] outline-none text-sm font-mono" />
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Color presets</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  data-testid={`qr-preset-${p.name.toLowerCase()}`}
                  onClick={() => { setFg(p.fg); setBg(p.bg); }}
                  className={`h-10 rounded-xl text-[11px] font-bold uppercase tracking-widest border transition ${fg === p.fg && bg === p.bg ? 'border-[#00F0FF] bg-white/10' : 'border-white/15 bg-white/5 hover:bg-white/10'}`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-full border border-white/20" style={{ background: p.fg }} />
                    {p.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Foreground</label>
              <div className="mt-2 flex items-center gap-2 bg-white/5 border border-white/15 rounded-xl px-2 h-11">
                <input type="color" value={fg} onChange={(e) => setFg(e.target.value)} className="h-7 w-9 bg-transparent cursor-pointer" />
                <input value={fg} onChange={(e) => setFg(e.target.value)} className="flex-1 bg-transparent outline-none text-sm font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Background</label>
              <div className="mt-2 flex items-center gap-2 bg-white/5 border border-white/15 rounded-xl px-2 h-11">
                <input type="color" value={bg} onChange={(e) => setBg(e.target.value)} className="h-7 w-9 bg-transparent cursor-pointer" />
                <input value={bg} onChange={(e) => setBg(e.target.value)} className="flex-1 bg-transparent outline-none text-sm font-mono" />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Size ({size}×{size})</label>
            <input type="range" min={200} max={1200} step={50} value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-full mt-2 accent-[#00F0FF]" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Error correction</label>
              <select value={ecc} onChange={(e) => setEcc(e.target.value)} className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#00F0FF] outline-none text-sm">
                <option value="L">Low</option>
                <option value="M">Medium</option>
                <option value="Q">Quartile</option>
                <option value="H">High (best for logos)</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Margin</label>
              <input type="number" min={0} max={20} value={margin} onChange={(e) => setMargin(Number(e.target.value))} className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-[#00F0FF] outline-none text-sm" />
            </div>
          </div>

          <Button data-testid="qr-download" onClick={download} className="w-full h-12 rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-black font-bold">
            <Download size={16} className="mr-2" /> Download PNG
          </Button>
        </div>

        {/* Preview */}
        <div className="flex items-center justify-center">
          <div className="relative">
            <div className="absolute -inset-10 rounded-3xl pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,240,255,0.25), transparent 60%)', filter: 'blur(20px)' }} />
            <div className="relative rounded-3xl p-8 glass-heavy">
              <img data-testid="qr-image" src={qrUrl} alt="QR" className="max-w-full h-auto rounded-xl" style={{ maxWidth: 460 }} />
              <div className="mt-4 text-center font-mono text-[10px] tracking-widest uppercase text-white/50">
                Powered by qrserver.com · Free forever
              </div>
            </div>
          </div>
        </div>
      </div>
      <Toaster />
      <EmailGate {...gate} />
    </div>
  );
}
